/* =========================================================================
   Product Gallery — fullscreen image lightbox
   Triggered by clicking on a .product-drawer__figure (drawer or page mode).
   Collects all images in the same media wrapper, lets user navigate with
   keyboard / nav buttons.
   ========================================================================= */
(function () {
  'use strict';

  class ProductGallery {
    constructor() {
      this.dialog = document.getElementById('ProductGallery');
      if (!this.dialog) return;

      this.image = this.dialog.querySelector('[data-product-gallery-image]');
      this.prevBtn = this.dialog.querySelector('[data-product-gallery-prev]');
      this.nextBtn = this.dialog.querySelector('[data-product-gallery-next]');
      this.closeBtn = this.dialog.querySelector('[data-product-gallery-close]');
      this.counter = this.dialog.querySelector('[data-product-gallery-counter]');
      this.currentEl = this.dialog.querySelector('[data-product-gallery-current]');
      this.totalEl = this.dialog.querySelector('[data-product-gallery-total]');

      this.stage = this.dialog.querySelector('[data-product-gallery-stage]');
      this.items = [];
      this.index = 0;
      this.lastFocused = null;
      this.isZoomed = false;
      this._panResetTimer = null;

      this._onTriggerClick = this._onTriggerClick.bind(this);
      this._onPrev = this._onPrev.bind(this);
      this._onNext = this._onNext.bind(this);
      this._onClose = this._onClose.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onBackdrop = this._onBackdrop.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onImageLoad = this._onImageLoad.bind(this);
      this._onStageClick = this._onStageClick.bind(this);
      this._onStageMove = this._onStageMove.bind(this);

      document.addEventListener('click', this._onTriggerClick);
      this.prevBtn.addEventListener('click', this._onPrev);
      this.nextBtn.addEventListener('click', this._onNext);
      this.closeBtn.addEventListener('click', this._onClose);
      this.dialog.addEventListener('click', this._onBackdrop);
      this.dialog.addEventListener('close', this._onDialogClose);
      this.dialog.addEventListener('keydown', this._onKey);
      this.image.addEventListener('load', this._onImageLoad);
      this.stage.addEventListener('click', this._onStageClick);
      this.stage.addEventListener('mousemove', this._onStageMove);
    }

    _onTriggerClick(event) {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;

      const figure = event.target.closest('.product-drawer__figure');
      if (!figure) return;
      // Don't hijack clicks inside the recommendations grid
      if (event.target.closest('.product-drawer__recommendations')) return;

      const mediaWrapper = figure.closest('.product-drawer__media');
      if (!mediaWrapper) return;

      const figures = Array.from(mediaWrapper.querySelectorAll('.product-drawer__figure'));
      const items = figures
        .map((fig) => {
          const img = fig.querySelector('img');
          if (!img) return null;
          return {
            src: this._highRes(img),
            srcset: img.getAttribute('srcset') || '',
            alt: img.getAttribute('alt') || '',
            sizes: '100vw',
          };
        })
        .filter(Boolean);

      if (items.length === 0) return;

      event.preventDefault();
      const initialIndex = Math.max(0, figures.indexOf(figure));
      this.open(items, initialIndex);
    }

    /**
     * Try to bump the image URL to a larger size for the lightbox.
     * Shopify image URLs include a `&width=NNN` or `width=NNN` param.
     */
    _highRes(img) {
      const base = img.currentSrc || img.src;
      if (!base) return '';
      // Replace width=NNN with width=2400
      return base.replace(/(\?|&)width=\d+/, '$1width=2400');
    }

    open(items, index) {
      this.items = items;
      this.index = index || 0;
      this.lastFocused = document.activeElement;

      this._render();

      if (!this.dialog.open) {
        this.dialog.showModal();
      }
      document.documentElement.classList.add('pd-no-scroll');
      document.body.classList.add('pd-no-scroll');

      // Move custom zoom cursor into this dialog's top-layer + switch icon
      const cursorEl = document.querySelector('[data-pd-cursor]');
      if (cursorEl) {
        if (cursorEl.parentNode !== this.dialog) {
          this.dialog.appendChild(cursorEl);
        }
        cursorEl.classList.add('in-gallery');
      }

      // Make the image element itself a zoom target so the custom cursor activates
      this.image.classList.add('product-drawer__figure');

      this.dialog.focus();
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    _onClose(event) {
      event.preventDefault();
      this.close();
    }

    _onPrev(event) {
      event.preventDefault();
      event.stopPropagation();
      this._go(-1);
    }

    _onNext(event) {
      event.preventDefault();
      event.stopPropagation();
      this._go(1);
    }

    _go(delta) {
      const n = this.items.length;
      if (n <= 1) return;
      this._setZoom(false);
      this.index = (this.index + delta + n) % n;
      this._render();
    }

    _setZoom(on, clientX, clientY) {
      if (on === this.isZoomed) return;
      if (on) {
        // Pivot the zoom on the click point
        const rect = this.image.getBoundingClientRect();
        const px = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
        const py = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
        this.image.style.transformOrigin = px + '% ' + py + '%';
        this.image.classList.add('is-zoomed');
        this.stage.classList.add('is-zoomed-stage');
        // Use snappy panning easing after the zoom-in transition completes
        clearTimeout(this._panResetTimer);
        this._panResetTimer = setTimeout(() => {
          this.image.classList.add('is-panning');
        }, 600);
      } else {
        this.image.classList.remove('is-zoomed', 'is-panning');
        this.stage.classList.remove('is-zoomed-stage');
        clearTimeout(this._panResetTimer);
        // After the unzoom transition, reset origin so the next zoom starts clean
        this._panResetTimer = setTimeout(() => {
          if (!this.isZoomed) {
            this.image.style.transformOrigin = '50% 50%';
          }
        }, 800);
      }
      this.isZoomed = on;
    }

    _onStageClick(event) {
      // Ignore clicks on the nav / close buttons
      if (event.target.closest('.product-gallery__close, .product-gallery__nav')) return;
      // Toggle zoom centered on the click point
      this._setZoom(!this.isZoomed, event.clientX, event.clientY);
    }

    _onStageMove(event) {
      if (!this.isZoomed) return;
      // Pan: map the cursor position over the stage to a transform-origin on
      // the image. We use the stage rect (not the image rect) so the pan
      // works whether the mouse is over the image or the surrounding stage.
      const stageRect = this.stage.getBoundingClientRect();
      const px = ((event.clientX - stageRect.left) / stageRect.width) * 100;
      const py = ((event.clientY - stageRect.top) / stageRect.height) * 100;
      const cx = Math.max(0, Math.min(100, px));
      const cy = Math.max(0, Math.min(100, py));
      this.image.style.transformOrigin = cx + '% ' + cy + '%';
    }

    _render() {
      const item = this.items[this.index];
      if (!item) return;
      this.image.classList.remove('is-loaded');
      this.image.removeAttribute('srcset');
      this.image.removeAttribute('sizes');
      this.image.src = item.src;
      if (item.srcset) this.image.setAttribute('srcset', item.srcset);
      if (item.sizes) this.image.setAttribute('sizes', item.sizes);
      this.image.alt = item.alt;

      const total = this.items.length;
      if (this.counter) this.counter.hidden = total <= 1;
      if (this.currentEl) this.currentEl.textContent = String(this.index + 1);
      if (this.totalEl) this.totalEl.textContent = String(total);

      this.prevBtn.disabled = total <= 1;
      this.nextBtn.disabled = total <= 1;
    }

    _onImageLoad() {
      this.image.classList.add('is-loaded');
    }

    _onBackdrop(event) {
      // Close only on direct dialog clicks (outside the stage entirely).
      // Stage clicks now toggle zoom, so we don't close from there.
      if (event.target === this.dialog) {
        this.close();
      }
    }

    _onKey(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this._go(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this._go(1);
      }
    }

    _onDialogClose() {
      // Reset zoom state on close
      this._setZoom(false);

      // Restore body scroll only if no other drawer is open
      const productDrawerOpen = document.getElementById('ProductDrawer')?.open;
      /* Il vecchio #ContactDrawer e' sparito (Task 8): il pannello boutique
         (#StoreLocatorDrawer) e' l'unico altro dialog che condivide
         pd-no-scroll, quindi e' lui a dover tenere il lock se e' ancora
         aperto quando questa galleria si chiude. */
      const storeLocatorOpen = document.getElementById('StoreLocatorDrawer')?.open;
      if (!productDrawerOpen && !storeLocatorOpen) {
        document.documentElement.classList.remove('pd-no-scroll');
        document.body.classList.remove('pd-no-scroll');
      }

      // Move cursor out of this dialog. If product drawer is open, put it
      // there; otherwise back into the body.
      const cursorEl = this.dialog.querySelector('[data-pd-cursor]');
      if (cursorEl) {
        cursorEl.classList.remove('in-gallery');
        const productDrawerDialog = document.getElementById('ProductDrawer');
        if (productDrawerDialog && productDrawerDialog.open) {
          productDrawerDialog.appendChild(cursorEl);
        } else {
          document.body.appendChild(cursorEl);
        }
      }

      this.image.classList.remove('product-drawer__figure');

      if (this.lastFocused && document.contains(this.lastFocused)) {
        this.lastFocused.focus({ preventScroll: true });
      }
      this.lastFocused = null;
    }
  }

  function init() {
    window.productGallery = new ProductGallery();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
